import { memo, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { styled } from '@mui/material/styles';
import { INFO_PANE_PAGES, openPatientPane } from 'redux_store';
import cx from 'classnames';
import { ViewSidebarOutlined as ViewSidebarIcon } from 'mui/icons';

/**
 * Cell-level wrapper that turns a value into a clickable affordance
 * which opens the InfoPane (right slide-in panel). Visual contract:
 *  - Default: same baseline as a normal cell (no pill, no icon).
 *    Light theme paints text in brand `--qhs-deep`; dark theme keeps
 *    `--qhs-primary` because `--qhs-deep` washes out on dark surfaces.
 *    Weight is `600` so the cell reads as "interactive" without an
 *    icon in the resting state.
 *  - Hover: soft `--qhs-surface-hover` background; text colour is
 *    held stable (no hue shift), and a small `ViewSidebar` glyph
 *    fades in on the right to telegraph "opens side panel" — the
 *    chevron read as "navigates to a new page".
 *
 * Behaviour preserved 1:1: same Redux dispatch (`openPatientPane`),
 * same `activeCellLink` className the parent `<tr>` uses to spot
 * whether a cell-level link is being hovered, same memoization on
 * `currentValue`. The legacy `openProgramPane` branch was removed
 * along with the Program InfoPane sub-page (S23, Phase 7 #20) — no
 * callsite ever passed `INFO_PANE_PAGES.program` to this wrapper.
 */

const PREFIX = 'InfoPaneWrapper';
const classes = {
  hasLink: `${PREFIX}-hasLink`,
  label: `${PREFIX}-label`,
  chevron: `${PREFIX}-chevron`,
};

const Root = styled('div')({
  display: 'contents',
  [`& .${classes.hasLink}`]: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    maxWidth: '100%',
    padding: '2px 6px',
    marginLeft: -6,
    borderRadius: 'var(--qhs-r-md)',
    background: 'transparent',
    color: 'var(--qhs-deep)',
    fontWeight: 600,
    lineHeight: 1.2,
    cursor: 'pointer',
    transition: 'background-color var(--qhs-dur-fast) var(--qhs-ease)',
    '&:hover': {
      background: 'var(--qhs-surface-hover)',
    },
  },
  [`[data-theme='dark'] & .${classes.hasLink}`]: {
    color: 'var(--qhs-primary)',
  },
  [`& .${classes.label}`]: {
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  [`& .${classes.chevron}`]: {
    display: 'block',
    width: 14,
    height: 14,
    flexShrink: 0,
    color: 'var(--qhs-text-3)',
    opacity: 0,
    // Transform-based reveal (slide+scale) was promoting every chevron
    // SVG into its own compositor layer because non-identity transform
    // values trigger layer promotion. On wide tables with 30-50 visible
    // InfoPane cells that turned every scroll frame into a 30-50 layer
    // recomposite — visibly janky scroll. Stick to opacity-only transition,
    // which the browser can paint without promoting.
    transition: 'opacity var(--qhs-dur-med) var(--qhs-ease)',
  },
  [`& .${classes.hasLink}:hover .${classes.chevron}`]: {
    opacity: 0.7,
  },
});

const InfoPaneWrapper = memo(({ infoPanePage, currentValue, row }) => {
  const dispatch = useDispatch();

  const onClick = useCallback(e => {
    e.preventDefault();
    e.stopPropagation();
    const newData = {
      accountUUID: row?.accountUUID,
      patientUUID: row?.patientUUID ?? row?.uuid,
      patientFirstName: row?.patientFirstName ?? row?.firstName,
      patientLastName: row?.patientLastName ?? row?.lastName,
      value: currentValue,
    };
    if (infoPanePage === INFO_PANE_PAGES.patient_page) {
      dispatch(openPatientPane(newData));
    }
    // eslint-disable-next-line
  }, []);

  return (
    <Root>
      <div onClick={onClick} className={cx(classes.hasLink, 'activeCellLink')}>
        <span className={classes.label}>{currentValue}</span>
        <ViewSidebarIcon className={classes.chevron} />
      </div>
    </Root>
  );
}, areEqual);

// `currentValue` is the rendered cell content — usually a string, but
// can be a React element. Reference equality is the right test: the
// parent recomputes `currentValue` only when `renderedCellValue`
// actually changes, so the same input produces the same reference.
function areEqual(prevProps, nextProps) {
  return prevProps.currentValue === nextProps.currentValue;
}

export default InfoPaneWrapper;
